/**
 * Grain Buds V2 (2026-08-24, per request) — Dawn's own localization-form.js
 * submits a real <form> when a country/currency is picked (needed so the
 * new prices/currency actually apply), which is a full page navigation, so
 * the browser drops the scroll position and lands back at the top. That
 * read fine when the picker only lived in the header/drawer near the top
 * of the page, but now that it's also in the footer, picking a country
 * there visibly "jumps" the page back up top. This restores the scroll
 * position across that navigation instead of touching Dawn's own
 * localization-form.js. Uses sessionStorage (survives the navigation,
 * clears itself once) rather than a URL param so it doesn't affect the
 * submitted form data or the resulting URL.
 *
 * 2026-08-24 follow-up: also owns the mobile hamburger drawer's body-
 * scroll lock. That was originally CSS-only (html/body overflow-y:hidden
 * via :has()), but on iOS Safari overflow:hidden doesn't fully stop
 * rubber-band scrolling and can report window.scrollY as 0 while
 * "locked" — which broke this same scroll-restore logic for the
 * Country/Currency picker now nested inside the drawer (picking a country
 * from inside the open drawer always "jumped" to the homepage top,
 * because the click handler below was reading the corrupted 0 instead of
 * the real pre-open scroll position) and let the announcement bar peek in
 * during overscroll. Locking via position:fixed on <body> with a negative
 * top offset is the standard iOS-safe fix, and as a side effect it gives
 * this script a reliable captured scroll value to use instead of
 * window.scrollY while the drawer is open.
 */
(function () {
  var STORAGE_KEY = 'gbLocalizationScrollY';
  var REOPEN_KEY = 'gbReopenMenuDrawer';
  var PIN_CLASS = 'gb-menu-pin';
  var CLOSE_ANIMATION_MS = 220; // Dawn's --duration-default is 200ms
  var lockedScrollY = null;
  var unpinTimer = null;

  function getSectionHeader() {
    return document.querySelector('.section-header');
  }

  function getAnnouncementBar() {
    return document.querySelector('.announcement-bar-section');
  }

  // 2026-08-24, per request — "keep the announcement bar visible above
  // the drawer instead of trying to time hiding/showing it perfectly".
  // Previously only .section-header pinned to top:0, which covered the
  // announcement bar entirely while the drawer was open; any mismatch
  // between when that cover appeared/disappeared and Dawn's own close
  // animation read as a flicker. Pinning the announcement bar too — at
  // true top:0, with the header measured and placed directly below it —
  // means the announcement bar is simply never covered in the first
  // place, so there's nothing left to flicker on close.
  function pinHeaderGroup() {
    var bar = getAnnouncementBar();
    var header = getSectionHeader();
    var barHeight = bar ? bar.offsetHeight : 0;
    document.documentElement.style.setProperty('--gb-announcement-height', barHeight + 'px');
    if (bar) bar.classList.add(PIN_CLASS);
    if (header) header.classList.add(PIN_CLASS);
  }

  function unpinHeaderGroup() {
    var bar = getAnnouncementBar();
    var header = getSectionHeader();
    if (bar) bar.classList.remove(PIN_CLASS);
    if (header) header.classList.remove(PIN_CLASS);
  }

  function lockScroll() {
    if (unpinTimer) {
      clearTimeout(unpinTimer);
      unpinTimer = null;
    }
    pinHeaderGroup();
    if (lockedScrollY !== null) return;
    lockedScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = -lockedScrollY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
  }

  function unlockScroll() {
    if (lockedScrollY === null) return;
    var y = lockedScrollY;
    lockedScrollY = null;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    window.scrollTo(0, y);
    // Keep the header/announcement bar pinned through the drawer's own
    // closing slide animation instead of snapping back in-flow the
    // instant [open] is removed — see the matching comment on
    // .gb-menu-pin in assets/gb-v2-header.css for why.
    unpinTimer = setTimeout(function () {
      unpinHeaderGroup();
      unpinTimer = null;
    }, CLOSE_ANIMATION_MS);
  }

  // Primary trigger: capture-phase click on the hamburger/close button
  // itself (2026-08-24, fix — the announcement-bar flicker on close was
  // still happening after the previous pass). Root cause: closing the
  // drawer goes through assets/global.js's MenuDrawer.closeMenuDrawer(),
  // which calls event.preventDefault() and removes the `.menu-opening`
  // class immediately (starting the ~200ms CSS slide-out right away) —
  // but it does NOT remove the <details> `open` attribute until its own
  // closeAnimation() finishes an internal ~400ms requestAnimationFrame
  // loop. The native 'toggle' event only fires once that attribute
  // actually changes, so listening for 'toggle' to unlock (as the
  // previous version did) fired ~400ms late, then added *another* 220ms
  // on top before un-pinning the header — by which point the drawer was
  // long gone but the header was still floating over the announcement
  // bar for no reason, then suddenly snapping away. Reading the *current*
  // open state on click, before Dawn's own handler runs, lets lock/unlock
  // happen in the same tick as the visual open/close instead.
  document.addEventListener(
    'click',
    function (event) {
      var summary = event.target.closest('.header__icon--menu');
      if (!summary) return;
      var details = document.getElementById('Details-menu-drawer-container');
      if (!details) return;
      if (details.hasAttribute('open')) {
        unlockScroll();
      } else {
        lockScroll();
      }
    },
    true
  );

  // Fallback for close paths that don't go through that click — Escape
  // key and focus-out both call closeMenuDrawer() directly
  // (assets/global.js). Same 'toggle'-event caveat as above applies here
  // (fires late, on Dawn's own attribute removal), so timing is less
  // precise for these rarer paths, but it keeps the lock from getting
  // stuck open if the drawer closes some other way.
  document.addEventListener(
    'toggle',
    function (event) {
      if (!event.target || event.target.id !== 'Details-menu-drawer-container') return;
      if (event.target.hasAttribute('open')) {
        lockScroll();
      } else {
        unlockScroll();
      }
    },
    true
  );

  var savedY = sessionStorage.getItem(STORAGE_KEY);
  if (savedY !== null) {
    sessionStorage.removeItem(STORAGE_KEY);
    var target = parseInt(savedY, 10);
    if (!isNaN(target)) {
      // Stop the browser's own scroll-restoration from fighting this.
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
      }
      // Hide the page (2026-08-24, per request — "these jump effects are
      // not acceptable, must stay in the exact same spot") until the
      // scroll position is corrected, instead of only racing to correct
      // it after paint. DOMContentLoaded fires long before the old
      // `load`-based correction did (which waited for every image to
      // finish, during which the page visibly sat at the top and then
      // jumped down), but there's still a small gap between it and first
      // paint on a full page navigation — hiding <html> for that gap
      // turns a visible top-then-jump into a plain reveal at the right
      // spot. The 400ms fallback timer guarantees the page is never left
      // permanently hidden if something above throws or DOMContentLoaded
      // never fires for some reason.
      document.documentElement.style.visibility = 'hidden';
      var revealed = false;
      function reveal() {
        if (revealed) return;
        revealed = true;
        document.documentElement.style.visibility = '';
      }
      document.addEventListener(
        'DOMContentLoaded',
        function () {
          window.scrollTo(0, target);
          reveal();
          reopenMenuDrawerIfNeeded();
        },
        { once: true }
      );
      // Layout (image heights, fonts, etc.) can still shift after
      // DOMContentLoaded, so re-apply once more once everything settles.
      window.addEventListener(
        'load',
        function () {
          window.scrollTo(0, target);
        },
        { once: true }
      );
      setTimeout(reveal, 400);
    }
  }

  // Re-open the hamburger drawer (and its Country/Currency panel) after a
  // page reload triggered by picking a country from inside it (2026-08-24,
  // per request — "clicked and it jumped to the homepage and doesn't go
  // back to the menu drawer page"). Restoring the scroll position alone
  // isn't enough here: the reload always lands with the drawer closed
  // (its open/closed state isn't part of the URL or any cookie, so a
  // fresh page load has no way to know it should be open), which reads
  // as "jumped to the homepage" even once the scroll position is right.
  function reopenMenuDrawerIfNeeded() {
    if (sessionStorage.getItem(REOPEN_KEY) === null) return;
    sessionStorage.removeItem(REOPEN_KEY);
    // A real .click() on the <summary>, not details.open = true (2026-08-24,
    // fix — the drawer's own <details> DID end up open with that, but
    // stayed visually invisible: Dawn's <menu-drawer> custom element
    // (assets/global.js, onSummaryClick/openMenuDrawer) only adds the
    // `.menu-opening` class — the thing that actually drives the
    // slide-in transform/visibility in component-menu-drawer.css — from
    // its own click handler on the <summary>, not from the generic
    // 'toggle' event. Setting the attribute/property directly skips that
    // handler entirely, so the drawer was "open" in the DOM but never
    // became visible. A real click runs Dawn's own logic exactly like a
    // user tapping the hamburger icon would.
    var summary = document.querySelector('.header__icon--menu');
    if (!summary) return;
    // Suppress the drawer's own slide-in transition for this one,
    // programmatic re-open (2026-08-24, per request — "landing back on
    // the menu drawer page shouldn't have a sliding animation"): it
    // should just already be there, not visibly "open" again. Dawn's own
    // MenuDrawer.openMenuDrawer (assets/global.js) adds the `.menu-
    // opening` class — the thing that actually drives the slide — via
    // its own zero-delay setTimeout, not synchronously inside the click,
    // so the class removal below is scheduled with a longer delay to run
    // strictly after that (setTimeout callbacks with later delays always
    // run after earlier-scheduled ones), keeping the transition
    // suppressed through the moment it would otherwise kick in.
    document.documentElement.classList.add('gb-no-drawer-transition');
    summary.click();
    setTimeout(function () {
      document.documentElement.classList.remove('gb-no-drawer-transition');
    }, 50);
    // Deliberately does NOT also reopen the Country/Currency panel itself
    // (2026-08-24, per request — "after picking, it should go straight
    // back to the menu drawer page, no need to manually close the picker
    // panel"): an earlier version re-clicked the trigger to restore that
    // panel too, but the ask is for picking a country to land back on the
    // plain drawer list, as if the picker had already been dismissed.
  }

  document.addEventListener('click', function (event) {
    var link = event.target.closest('localization-form .disclosure__link');
    if (link) {
      var y = lockedScrollY !== null ? lockedScrollY : window.scrollY || window.pageYOffset || 0;
      sessionStorage.setItem(STORAGE_KEY, String(y));
      if (link.closest('.menu-drawer-container[open]')) {
        sessionStorage.setItem(REOPEN_KEY, '1');
      }
    }
  });
})();
